import { createFileRoute, redirect } from "@tanstack/react-router";
import { z } from "zod";
import MfaPage from "~/pages/MfaPage";
import { queryClient, trpc } from "~/utils/trpc";
import { isMcpOAuthRequest } from "~/utils/openapi-oauth";

const mfaSearchParams = z.object({
  setup: z
    .preprocess((value) => {
      if (value === "true") return true;
      if (value === "false") return false;
      return value;
    }, z.boolean())
    .optional(),
  redirectTo: z
    .string()
    .refine((v) => v.startsWith("/") && !v.startsWith("//"), {
      message: "redirectTo must be a relative path",
    })
    .optional(),
  oauth: z
    .object({
      client_id: z.string(),
      redirect_uri: z.string().url().optional(),
      response_type: z.literal("code"),
      scope: z.string().optional(),
      state: z.string().optional(),
      channel: z.enum(["chrome_extension"]).optional(),
      code_challenge: z.string().optional(),
      code_challenge_method: z.enum(["S256"]).optional(),
      resource: z.string().url().optional(),
    })
    .superRefine((value, ctx) => {
      if (value.channel !== "chrome_extension" && !value.redirect_uri) {
        ctx.addIssue({
          code: "custom",
          path: ["redirect_uri"],
          message: "redirect_uri is required",
        });
      }
      if (value.channel === "chrome_extension" && !value.code_challenge) {
        ctx.addIssue({
          code: "custom",
          path: ["code_challenge"],
          message: "code_challenge is required",
        });
      }
    })
    .optional(),
});

export const Route = createFileRoute("/_public/mfa")({
  validateSearch: mfaSearchParams,
  beforeLoad: async ({ location, search }) => {
    const session = await queryClient.fetchQuery(trpc.auth.getSession.queryOptions()).catch(() => null);
    if (!session?.isLoggedIn) {
      throw redirect({ to: "/login", search: { redirectTo: location.href } });
    }
    if (session.authStage === "first_factor") {
      throw redirect({
        to: "/select-dataset",
        search: {
          ...(search.oauth ? { oauth: search.oauth } : {}),
          ...(search.redirectTo ? { redirectTo: search.redirectTo } : {}),
        },
      });
    }
    if (session.authStage === "authenticated" && !search.setup) {
      if (search.oauth && isMcpOAuthRequest(search.oauth)) {
        throw redirect({ to: "/openapi-oauth", search: search.oauth });
      }
      throw redirect({
        to: search.redirectTo || (session.slug ? `/${session.slug}/chats` : "/"),
      });
    }
  },
  component: MfaPage,
});
